import { DisplayValue } from '@/components/Common/DisplayData';

import { ExternalLinkIcon } from 'lucide-react';
import { Link, LinkProps } from 'react-router-dom';

type LinkToEntityProps = {
  name: string;
  openInNewTab?: boolean;
  linkTo: LinkProps['to'];
};

const LinkToEntity = ({ name, openInNewTab, linkTo }: LinkToEntityProps) => (
  <Link
    to={linkTo}
    className="flex items-center gap-1 text-primary-500 hover:underline"
    target={openInNewTab ? '_blank' : undefined}
  >
    {name}
    {openInNewTab && <ExternalLinkIcon className="h-3 w-3" />}
  </Link>
);

type MongoEntity = {
  /** @deprecated Use the `_id` prop instead. */
  _id: string;
  /** @deprecated Use the `name` prop instead. */
  name: string;
};

type Entity = {
  /** The entity's ID. */
  id: string;
  /** The entity's attributes. */
  attributes: {
    /** The entity's name. */
    name: string;
  };
};

type EntityCardProps = {
  /** The entity to display. */
  entity: Entity | MongoEntity;
  /** The class name to apply to the entity card. */
  className?: string;

  linkTo?: {
    /** The path to the entity. */
    path: string;
    /** Whether to open the link in a new tab. */
    openInNewTab?: boolean;
  };
};

/**
 * Displays an entity's name and ID, with a link to the entity and the ability to copy the ID.
 * Handles both legacy Mongo entities and new entity objects as defined in the Kard API standards.
 */
export const EntityCard = ({ entity, linkTo, className }: EntityCardProps) => {
  const id = 'id' in entity ? entity.id : entity._id;
  const name = 'attributes' in entity ? entity.attributes.name : entity.name;

  const label = linkTo ? (
    <LinkToEntity
      name={name}
      openInNewTab={linkTo.openInNewTab}
      linkTo={linkTo.path}
    />
  ) : (
    name
  );

  return (
    <DisplayValue className={className} label={label} type="id" value={id} />
  );
};
